import type { BookMeta, RichSection } from '../types/book'
import { calculateHashFromString } from './indexedDB'

export const bookComparisonVersion = 1
export const defaultBookComparisonShingleSize = 5
export const defaultBookComparisonFingerprintSize = 128

export const bookComparisonThresholds = {
  likelySameBook: 0.45,
  highSimilarity: 0.8,
}

export type BookComparisonFields = Pick<
  BookMeta,
  | 'comparisonVersion'
  | 'normalizedTextHash'
  | 'coreTextHash'
  | 'shingleFingerprint'
  | 'normalizedSectionHashes'
  | 'textTokenCount'
  | 'spineItemCount'
>

export type BookComparisonResult = {
  exactTextMatch: boolean
  fuzzySimilarity: number
  likelySameBook: boolean
  likelyDifferentEdition: boolean
  cfiPortabilityScore: number | null
  note: string
}

export type CfiPortabilitySample = {
  cfiRange: string
  text: string
}

export type CfiPortabilityResult = {
  portabilityScore: number
  matchedSamples: number
  totalSamples: number
  note: string
}

type ComparisonSection = Pick<RichSection, 'href' | 'html' | 'text'>

const frontMatterHints = [
  'contents',
  'table of contents',
  'toc',
  'copyright',
  'dedication',
  'epigraph',
  'preface',
  'foreword',
  'introduction',
]

const backMatterHints = [
  'acknowledgements',
  'acknowledgments',
  'about the author',
  'author bio',
  'endnotes',
  'notes',
  'bibliography',
  'appendix',
  'index',
]

const collapseWhitespace = (value: string): string =>
  value.replace(/[\u00a0\u1680\u2000-\u200d\u2028\u2029\u202f\u205f\u3000]+/g, ' ').replace(/\s+/g, ' ').trim()

const extractComparisonTokens = (value: string): string[] => {
  const canonical = collapseWhitespace(
    value
      .normalize('NFKC')
      .toLowerCase()
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201c\u201d]/g, '"')
      .replace(/[\u200b-\u200f\u2060\ufeff]/g, ' ')
  )

  return canonical.match(/[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu) || []
}

const hashShingle = (value: string): string => {
  let hash = 2166136261
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

const trimContextWindow = (value: string, maxChars: number, fromStart: boolean): string => {
  if (value.length <= maxChars) {
    return value
  }

  return fromStart ? value.slice(0, maxChars) : value.slice(-maxChars)
}

const hasHint = (value: string, hints: string[]): boolean =>
  hints.some((hint) => value === hint || value.startsWith(`${hint} `))

const isLikelyNavigationSection = (section: ComparisonSection, normalizedText: string, tokenCount: number): boolean => {
  if (!normalizedText) {
    return true
  }

  const href = (section.href || '').toLowerCase()
  const html = (section.html || '').toLowerCase()
  if (/<nav\b/.test(html) || href.includes('toc') || href.includes('nav')) {
    return true
  }

  if (tokenCount <= 40 && hasHint(normalizedText, ['contents', 'table of contents', 'toc', 'navigation'])) {
    return true
  }

  return false
}

const shouldTrimPeripheralSection = (
  normalizedText: string,
  tokenCount: number,
  href: string,
  direction: 'front' | 'back'
): boolean => {
  if (!normalizedText) {
    return true
  }

  const loweredHref = href.toLowerCase()
  const hints = direction === 'front' ? frontMatterHints : backMatterHints
  const hintMatch = hasHint(normalizedText, hints) || hints.some((hint) => loweredHref.includes(hint.replace(/\s+/g, '')))

  if (!hintMatch) {
    return false
  }

  return tokenCount <= 250
}

const buildShingleFingerprint = (
  tokens: string[],
  shingleSize = defaultBookComparisonShingleSize,
  fingerprintSize = defaultBookComparisonFingerprintSize
): string[] => {
  if (tokens.length === 0) {
    return []
  }

  if (tokens.length < shingleSize) {
    return [hashShingle(tokens.join(' '))]
  }

  const hashes = new Set<string>()
  for (let i = 0; i <= tokens.length - shingleSize; i += 1) {
    hashes.add(hashShingle(tokens.slice(i, i + shingleSize).join(' ')))
  }

  return Array.from(hashes).sort().slice(0, fingerprintSize)
}

const roundSimilarity = (value: number): number => Math.round(value * 1000) / 1000

const buildExplanation = (
  exactTextMatch: boolean,
  coreTextMatch: boolean,
  fuzzySimilarity: number
): string => {
  if (exactTextMatch) {
    return 'Exact normalized text hash match.'
  }

  if (coreTextMatch) {
    return 'Core text matches after trimming likely front/back matter.'
  }

  if (fuzzySimilarity >= bookComparisonThresholds.highSimilarity) {
    return 'High fuzzy shingle overlap; likely the same book text with edition or packaging differences.'
  }

  if (fuzzySimilarity >= bookComparisonThresholds.likelySameBook) {
    return 'Moderate fuzzy shingle overlap; likely the same work with different edition matter or structure.'
  }

  return 'Low fuzzy shingle overlap; likely a different book.'
}

export const normalizeBookComparisonText = (value: string): string => extractComparisonTokens(value).join(' ')

export const tokenizeBookComparisonText = (value: string): string[] => extractComparisonTokens(value)

export const buildBookComparisonFieldsFromSections = async (
  sections: ComparisonSection[]
): Promise<BookComparisonFields> => {
  const normalizedSections = sections.map((section) => {
    const normalizedText = normalizeBookComparisonText(section.text || '')
    const tokenCount = normalizedText ? normalizedText.split(' ').length : 0
    if (isLikelyNavigationSection(section, normalizedText, tokenCount)) {
      return {
        href: section.href || '',
        normalizedText: '',
        tokenCount: 0,
      }
    }

    return {
      href: section.href || '',
      normalizedText,
      tokenCount,
    }
  })

  const normalizedSectionHashes = await Promise.all(
    normalizedSections.map(async ({ normalizedText }) =>
      normalizedText ? calculateHashFromString(normalizedText) : ''
    )
  )

  const nonEmptySections = normalizedSections.filter((section) => section.normalizedText)
  const fullText = nonEmptySections.map((section) => section.normalizedText).join('\n')
  const fullTextTokens = fullText ? fullText.split(' ') : []

  let coreStart = 0
  let coreEnd = normalizedSections.length - 1
  let trimBudget = Math.min(3, normalizedSections.length)

  while (
    coreStart < coreEnd &&
    trimBudget > 0 &&
    shouldTrimPeripheralSection(
      normalizedSections[coreStart]?.normalizedText || '',
      normalizedSections[coreStart]?.tokenCount || 0,
      normalizedSections[coreStart]?.href || '',
      'front'
    )
  ) {
    coreStart += 1
    trimBudget -= 1
  }

  trimBudget = Math.min(3, normalizedSections.length)
  while (
    coreEnd > coreStart &&
    trimBudget > 0 &&
    shouldTrimPeripheralSection(
      normalizedSections[coreEnd]?.normalizedText || '',
      normalizedSections[coreEnd]?.tokenCount || 0,
      normalizedSections[coreEnd]?.href || '',
      'back'
    )
  ) {
    coreEnd -= 1
    trimBudget -= 1
  }

  const coreSections = normalizedSections
    .slice(coreStart, coreEnd + 1)
    .map((section) => section.normalizedText)
    .filter(Boolean)
  const coreText = coreSections.join('\n') || fullText

  return {
    comparisonVersion: bookComparisonVersion,
    normalizedTextHash: await calculateHashFromString(fullText),
    coreTextHash: await calculateHashFromString(coreText),
    shingleFingerprint: buildShingleFingerprint(fullTextTokens),
    normalizedSectionHashes,
    textTokenCount: fullTextTokens.length,
    spineItemCount: sections.length,
  }
}

export const hasCurrentBookComparisonFields = (
  book: Partial<BookMeta>,
  expectedSectionCount?: number
): boolean => {
  if (book.comparisonVersion !== bookComparisonVersion) {
    return false
  }

  if (
    typeof book.normalizedTextHash !== 'string' ||
    typeof book.coreTextHash !== 'string' ||
    !Array.isArray(book.shingleFingerprint) ||
    !Array.isArray(book.normalizedSectionHashes) ||
    typeof book.textTokenCount !== 'number' ||
    typeof book.spineItemCount !== 'number'
  ) {
    return false
  }

  if (expectedSectionCount !== undefined && book.normalizedSectionHashes.length !== expectedSectionCount) {
    return false
  }

  return true
}

export const summarizeBookComparisonForDebug = (book?: Partial<BookMeta> | null) => ({
  comparisonVersion: book?.comparisonVersion,
  normalizedTextHash: book?.normalizedTextHash?.slice(0, 12),
  coreTextHash: book?.coreTextHash?.slice(0, 12),
  shingleFingerprintCount: book?.shingleFingerprint?.length || 0,
  normalizedSectionHashCount: book?.normalizedSectionHashes?.length || 0,
  textTokenCount: book?.textTokenCount,
  spineItemCount: book?.spineItemCount,
})

export const compareStoredBookComparison = (
  left: Partial<BookMeta>,
  right: Partial<BookMeta>,
  cfiPortabilityScore: number | null = null
): BookComparisonResult => {
  const exactTextMatch =
    Boolean(left.normalizedTextHash) &&
    left.normalizedTextHash === right.normalizedTextHash
  const coreTextMatch =
    !exactTextMatch && Boolean(left.coreTextHash) && left.coreTextHash === right.coreTextHash

  const leftFingerprint = new Set(left.shingleFingerprint || [])
  const rightFingerprint = new Set(right.shingleFingerprint || [])
  const union = new Set([...leftFingerprint, ...rightFingerprint])
  const intersectionCount = Array.from(leftFingerprint).filter((hash) => rightFingerprint.has(hash)).length
  const fuzzySimilarity = union.size > 0 ? roundSimilarity(intersectionCount / union.size) : 0
  const likelySameBook =
    exactTextMatch ||
    coreTextMatch ||
    fuzzySimilarity >= bookComparisonThresholds.likelySameBook
  const likelyDifferentEdition = !exactTextMatch && likelySameBook

  return {
    exactTextMatch,
    fuzzySimilarity,
    likelySameBook,
    likelyDifferentEdition,
    cfiPortabilityScore,
    note: buildExplanation(exactTextMatch, coreTextMatch, fuzzySimilarity),
  }
}

export const scoreCfiPortability = async (
  samples: CfiPortabilitySample[],
  resolveTargetText: (cfiRange: string) => string | null | Promise<string | null>
): Promise<CfiPortabilityResult> => {
  if (samples.length === 0) {
    return {
      portabilityScore: 0,
      matchedSamples: 0,
      totalSamples: 0,
      note: 'No CFI samples were available to evaluate portability.',
    }
  }

  let matchedSamples = 0
  for (const sample of samples) {
    const resolvedText = await resolveTargetText(sample.cfiRange)
    if (!resolvedText) {
      continue
    }

    if (normalizeBookComparisonText(resolvedText) === normalizeBookComparisonText(sample.text)) {
      matchedSamples += 1
    }
  }

  const portabilityScore = roundSimilarity(matchedSamples / samples.length)
  let note = 'Low direct CFI compatibility; relocate annotations by text/context.'
  if (portabilityScore >= 0.85) {
    note = 'High direct CFI compatibility; annotations are likely reusable as-is.'
  } else if (portabilityScore >= 0.5) {
    note = 'Mixed direct CFI compatibility; keep provenance and prefer cautious relocation.'
  }

  return {
    portabilityScore,
    matchedSamples,
    totalSamples: samples.length,
    note,
  }
}

export const summarizeSectionContext = (value: string, maxChars = 160): { before: string; after: string } => {
  const normalized = collapseWhitespace(value)
  return {
    before: trimContextWindow(normalized, maxChars, false),
    after: trimContextWindow(normalized, maxChars, true),
  }
}
